import { useDispatch, useSelector } from "react-redux";
import Modal from "../UI/Modal";
import {
  closeFormModal,
  openViewModal,
} from "../../store/reducers/users/users.reducer";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  getEscuelasThunk,
  getPlantelesThunk,
  getUserByIdThunk,
  getUsersThunk,
  updateUserThunk,
} from "../../store/reducers/users/users.thunks";
import Loader from "../UI/Loader";
import Alert from "../UI/Alert";
import { toast } from "react-toastify";

const UserEditModal = () => {
  const dispatch = useDispatch();
  const {
    formModalOpen,
    formLoading,
    formError,
    selectedUser,
    page,
    search,
    escuelas,
    planteles,
  } = useSelector((state) => state.users);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onSubmit" });
  // 🔹 Cargar escuelas al abrir modal
  useEffect(() => {
    if (formModalOpen) {
      dispatch(getEscuelasThunk());
    }
  }, [formModalOpen, dispatch]);
  useEffect(() => {
    if (formModalOpen && selectedUser) {
      const escuelaId = selectedUser.escuela_id || selectedUser.escuela;
      const plantelId = selectedUser.plantel_id || selectedUser.plantel;

      reset({
        username: selectedUser.username || "",
        email: selectedUser.email || "",
        nombres: selectedUser.nombres || "",
        apellido_paterno: selectedUser.apellido_paterno || "",
        apellido_materno: selectedUser.apellido_materno || "",
        rol: selectedUser.rol || "",
        telefono: selectedUser.telefono || "",
        correo: selectedUser.correo || "",
        estado_civil: selectedUser.estado_civil || "",
        curp: selectedUser.curp || "",
        rfc: selectedUser.rfc || "",
        escuela: escuelaId || "",
        plantel: plantelId || "",
      });

      if (escuelaId) {
        dispatch(getPlantelesThunk(escuelaId));
      }
    }
  }, [formModalOpen, selectedUser, dispatch, reset]);

  // 🔹 Cuando cambia escuela
  const handleEscuelaChange = (e) => {
    const escuelaId = Number(e.target.value);

    setValue("escuela", escuelaId, { shouldValidate: true });
    setValue("plantel", "", { shouldValidate: true });

    if (escuelaId) {
      dispatch(getPlantelesThunk(escuelaId));
    }
  };

  // 🔹 Submit update
  const onSubmit = async (data) => {
    const payload = {
      username: data.username,
      email: data.email,
      nombres: data.nombres,
      apellido_paterno: data.apellido_paterno,
      apellido_materno: data.apellido_materno,
      rol: data.rol,
      telefono: data.telefono,
      correo: data.correo,
      estado_civil: data.estado_civil,
      curp: data.curp,
      rfc: data.rfc,
      plantel: Number(data.plantel) || null,
      escuela: Number(data.escuela) || null,
    };

    const result = await dispatch(
      updateUserThunk({ id: selectedUser.id, payload }),
    );

    if (result.meta.requestStatus === "fulfilled") {
      dispatch(getUsersThunk({ page, search }));
      toast.success("Usuario actualizado exitosamente");
      await dispatch(getUserByIdThunk(selectedUser.id));
      dispatch(openViewModal());
    }
  };

  return (
    <Modal
      open={formModalOpen}
      onClose={() => dispatch(closeFormModal())}
      title="Editar usuario"
      size="lg"
    >
      {formLoading ? (
        <Loader center type="dots" text="Actualizando usuario..." />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="px-2 px-md-3">
          {formError && <Alert message={formError} variant="error" />}

          {/* ── Sección: Cuenta ── */}
          <p className="fw-bold text-primary mb-3 mt-2 fs-6 border-bottom pb-1">
            Información de cuenta
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Usuario <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${
                  errors.username ? "is-invalid" : ""
                }`}
                placeholder="Ej: jperez"
                {...register("username", {
                  required: "El usuario es requerido",
                })}
              />
              {errors.username && (
                <div className="invalid-feedback">
                  {errors.username.message}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Email <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${
                  errors.email ? "is-invalid" : ""
                }`}
                placeholder="Ej: jperez@mail.com"
                {...register("email", {
                  required: "El email es requerido",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Email no válido",
                  },
                })}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Rol <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${
                  errors.rol ? "is-invalid" : ""
                }`}
                {...register("rol", { required: "El rol es obligatorio" })}
              >
                <option value="" disabled>
                  Selecciona un rol
                </option>
                <option value="ADMIN SYS">Administrador global</option>
                <option value="MENTOR">Mentor</option>
                <option value="ESTUDIANTE">Estudiante</option>
                <option value="ADMIN_ESCOLAR">
                  Auxiliar de control escolar
                </option>
              </select>
              {errors.rol && (
                <div className="invalid-feedback">{errors.rol.message}</div>
              )}
            </div>
          </div>

          {/* ── Sección: Datos personales ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Datos personales
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Nombres <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${
                  errors.nombres ? "is-invalid" : ""
                }`}
                placeholder="Ej: Juan"
                {...register("nombres", {
                  required: "El nombre es obligatorio",
                  minLength: { value: 2, message: "Mínimo 2 caracteres" },
                })}
              />
              {errors.nombres && (
                <div className="invalid-feedback">{errors.nombres.message}</div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Apellido paterno <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${
                  errors.apellido_paterno ? "is-invalid" : ""
                }`}
                placeholder="Ej: Pérez"
                {...register("apellido_paterno", {
                  required: "El apellido paterno es obligatorio",
                })}
              />
              {errors.apellido_paterno && (
                <div className="invalid-feedback">
                  {errors.apellido_paterno.message}
                </div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Apellido materno <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${
                  errors.apellido_materno ? "is-invalid" : ""
                }`}
                placeholder="Ej: García"
                {...register("apellido_materno", {
                  required: "El apellido materno es obligatorio",
                })}
              />
              {errors.apellido_materno && (
                <div className="invalid-feedback">
                  {errors.apellido_materno.message}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Estado civil <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${
                  errors.estado_civil ? "is-invalid" : ""
                }`}
                {...register("estado_civil", {
                  required: "El estado civil es obligatorio",
                })}
              >
                <option value="" disabled>
                  Estado civil
                </option>
                <option value="SOLTERO">Soltero/a</option>
                <option value="CASADO">Casado/a</option>
                <option value="DIVORCIADO">Divorciado/a</option>
                <option value="VIUDO">Viudo/a</option>
                <option value="UNION_LIBRE">En unión libre</option>
                <option value="SEPARADO">Separado/a</option>
              </select>
              {errors.estado_civil && (
                <div className="invalid-feedback">
                  {errors.estado_civil.message}
                </div>
              )}
            </div>
          </div>

          {/* ── Sección: Contacto ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Contacto
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">Teléfono</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: 5512345678"
                {...register("telefono")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Correo alternativo
              </label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: otro@mail.com"
                {...register("correo")}
              />
            </div>
          </div>

          {/* ── Sección: Institución ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Institución
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Escuela <span className="text-danger">*</span>
              </label>

              <select
                className={`form-select shadow-sm rounded-3 ${
                  errors.escuela ? "is-invalid" : ""
                }`}
                {...register("escuela", {
                  required: "La escuela es obligatoria",
                })}
                onChange={handleEscuelaChange}
              >
                <option value="">Selecciona una escuela</option>
                {escuelas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.label}
                  </option>
                ))}
              </select>

              {errors.escuela && (
                <div className="invalid-feedback">{errors.escuela.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Plantel <span className="text-danger">*</span>
              </label>

              <select
                className={`form-select shadow-sm rounded-3 ${
                  errors.plantel ? "is-invalid" : ""
                }`}
                {...register("plantel", {
                  required: "El plantel es obligatorio",
                })}
                disabled={!planteles.length}
              >
                <option value="">Selecciona un plantel</option>
                {planteles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>

              {errors.plantel && (
                <div className="invalid-feedback">{errors.plantel.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">CURP</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: PEGJ900101HDFRZN01"
                {...register("curp")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">RFC</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: PEGJ900101ABC"
                {...register("rfc")}
              />
            </div>
          </div>

          {/* ── Acciones ── */}
          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light border rounded-3 px-4"
              onClick={() => dispatch(closeFormModal())}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-3 px-4 shadow-sm"
            >
              Guardar cambios
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UserEditModal;
